/**
 * WHAT THE POLICY LIBRARY ASKS FOR.
 *
 * One description of a view of the library — which policies, narrowed how,
 * in what order, how many at a time — and two translations of it: a database
 * query when nobody has typed anything, and a search-index query when they
 * have. The page is one experience over two engines, so the description is
 * the single thing both are built from.
 *
 * THE DATABASE QUERY IS SHAPED BY THE ACCESS RULES. A list is allowed only
 * when the query itself proves every row readable, so every shape here pins
 * `status` to values the person's role may read, and nothing a person puts
 * in the address bar can widen that: an unknown view, a filter they are not
 * offered or a sort they are not offered falls back to what they are.
 * tests/firestore-rules.test.js runs these exact shapes, allowed and
 * forbidden, from tests/fixtures/library-query-shapes.json.
 *
 * NOTHING HERE LOADS A LIBRARY. Every shape is ordered and takes a page size;
 * the adapter adds the limit and the cursor.
 *
 * Pure. No Firestore, no network, and the clock is an argument.
 */

import { hasAtLeast, type Role } from '../../../types/roles';

export const LIBRARY_VIEWS = [
  'all', 'published', 'inReview', 'drafts', 'retired',
  // Conditions that need somebody's attention.
  'reviewOverdue', 'reviewDue',
  // Two queues, never one: nobody was ever assigned; or somebody was, and has left.
  'ownerUnassigned', 'ownerInactive',
] as const;
export type LibraryView = (typeof LIBRARY_VIEWS)[number];

export const LIBRARY_SORTS = ['title', 'number', 'review', 'updated'] as const;
export type LibrarySort = (typeof LIBRARY_SORTS)[number];

export const PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

export interface LibraryState {
  view: LibraryView;
  /** Policies the reader owns. Owner, and nothing else: not approvals, not authorship. */
  mine: boolean;
  department: string | null;
  category: string | null;
  documentType: string | null;
  owner: string | null;
  sort: LibrarySort;
  dir: 'asc' | 'desc';
  size: PageSize;
  /** What was typed. Not empty: the search index answers, by relevance. */
  text: string;
  /**
   * A named handful of policies: "the twelve I just uploaded". When set, the
   * page shows exactly these and nothing is filtered, sorted or paged.
   */
  ids: string[];
}

export interface Who {
  uid: string;
  roles: readonly Role[];
  /** The organization's own setting; the rule refuses the query otherwise. */
  mayReadRetired: boolean;
}

/** Statuses of a policy still in play: what people who work on policies may list together. */
export const ACTIVE_STATUSES = ['draft', 'in_review', 'pending_review', 'approved', 'published'] as const;
/** `in_review` is what submission writes; the other two are older spellings still on some records. */
export const IN_REVIEW_STATUSES = ['in_review', 'pending_review', 'approved'] as const;

const worksOnPolicies = (who: Who) => hasAtLeast(who.roles, 'author');

/** The views this person may open. A reader's library is the published one. */
export function viewsFor(who: Who): LibraryView[] {
  const views: LibraryView[] = worksOnPolicies(who)
    ? ['all', 'published', 'inReview', 'drafts', 'reviewOverdue', 'reviewDue']
    : ['published'];
  if (who.mayReadRetired) views.push('retired');
  if (hasAtLeast(who.roles, 'admin')) views.push('ownerUnassigned', 'ownerInactive');
  return views;
}

/** The orders this person is offered. Review dates are not a reader's concern. */
export function sortsFor(who: Who): LibrarySort[] {
  return worksOnPolicies(who) ? ['title', 'number', 'review', 'updated'] : ['title', 'number'];
}

/** The two owner queues, which are read from the ownership records and not from the policies. */
export function isOwnerQueue(view: LibraryView): view is 'ownerUnassigned' | 'ownerInactive' {
  return view === 'ownerUnassigned' || view === 'ownerInactive';
}

/**
 * Whether this person is shown who owns a policy at all: the Owner column,
 * the Owner filter, an owner's state, the address an import is waiting for.
 * Not a reader. The database refuses them the ownership records as well
 * (firestore.rules, policyOwnership); this decides what is asked for.
 */
export function seesOwners(who: Who): boolean {
  return worksOnPolicies(who);
}

export function defaultView(who: Who): LibraryView {
  return worksOnPolicies(who) ? 'all' : 'published';
}

export function defaultState(who: Who): LibraryState {
  return {
    view: defaultView(who), mine: false,
    department: null, category: null, documentType: null, owner: null,
    sort: 'title', dir: 'asc', size: DEFAULT_PAGE_SIZE, text: '', ids: [],
  };
}

/**
 * What was asked for, made into something this person may have. The address
 * bar is input like any other.
 */
export function settle(state: LibraryState, who: Who): LibraryState {
  const view = viewsFor(who).includes(state.view) ? state.view : defaultView(who);
  const reviewList = view === 'reviewOverdue' || view === 'reviewDue';
  const queue = isOwnerQueue(view);
  const sort: LibrarySort = reviewList ? 'review'
    // A queue is ordered by title or number: that is what its records carry.
    : queue ? (state.sort === 'number' ? 'number' : 'title')
      : sortsFor(who).includes(state.sort) ? state.sort : 'title';
  const author = worksOnPolicies(who);
  // A queue is the whole of its condition. The other filters are not offered
  // on it, and one carried over in the address is dropped, not half-applied.
  if (queue) {
    // One exception: the people who left. "Reassign Ann's policies" is the
    // task this queue exists for, and with two leavers it cannot be done
    // unless the queue can be narrowed to one of them.
    const owner = view === 'ownerInactive' ? state.owner : null;
    return { ...state, view, sort, mine: false, department: null, category: null, documentType: null, owner };
  }
  return {
    ...state,
    view,
    sort,
    // By review date is soonest first, and only that: a policy with no review
    // date sorts after every real one, which latest-first would put on top.
    dir: sort === 'review' ? 'asc' : state.dir,
    mine: author && state.mine,
    // Mine IS an owner filter; the two together would only ever contradict.
    owner: author && !state.mine ? state.owner : null,
  };
}

// ---------------------------------------------------------------- the address

const ID = /^[A-Za-z0-9_-]{1,128}$/;
const id = (v: string | null): string | null => (v && ID.test(v) ? v : null);
export const TEXT_MAX = 200;

/** The most ids a named set carries in the address: what one bulk upload can make. */
export const MAX_NAMED_IDS = 100;

function parseIds(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(',')) {
    const one = raw.trim();
    if (ID.test(one)) seen.add(one);
    if (seen.size >= MAX_NAMED_IDS) break;
  }
  return [...seen];
}

/**
 * What the page called its sets before it was paged (`?scope=overdue`).
 * Bookmarks and links in old emails still say it, and should land on the
 * same thing, not on the default view.
 */
const LEGACY_SCOPE: Record<string, LibraryView> = {
  all: 'all', published: 'published', inReview: 'inReview', drafts: 'drafts', retired: 'retired',
  overdue: 'reviewOverdue', expiring: 'reviewDue', formerOwner: 'ownerInactive',
};

export function parseLibraryState(params: URLSearchParams, who: Who): LibraryState {
  const base = defaultState(who);
  const view = (params.get('view') ?? LEGACY_SCOPE[params.get('scope') ?? ''] ?? null) as LibraryView | null;
  const sort = params.get('sort') as LibrarySort | null;
  const size = Number(params.get('size'));
  return settle({
    view: view && (LIBRARY_VIEWS as readonly string[]).includes(view) ? view : base.view,
    mine: params.get('mine') === '1',
    department: id(params.get('dept')),
    category: id(params.get('cat')),
    documentType: id(params.get('type')),
    owner: id(params.get('owner')),
    sort: sort && (LIBRARY_SORTS as readonly string[]).includes(sort) ? sort : base.sort,
    dir: params.get('dir') === 'desc' ? 'desc' : 'asc',
    size: (PAGE_SIZES as readonly number[]).includes(size) ? (size as PageSize) : base.size,
    text: (params.get('q') ?? '').slice(0, TEXT_MAX),
    ids: parseIds(params.get('ids')),
  }, who);
}

/** Only what differs from the default, so a plain link stays a plain link. */
export function libraryParams(state: LibraryState, who: Who): URLSearchParams {
  const base = defaultState(who);
  const s = settle(state, who);
  const out = new URLSearchParams();
  if (s.view !== base.view) out.set('view', s.view);
  if (s.mine) out.set('mine', '1');
  if (s.department) out.set('dept', s.department);
  if (s.category) out.set('cat', s.category);
  if (s.documentType) out.set('type', s.documentType);
  if (s.owner) out.set('owner', s.owner);
  if (s.sort !== base.sort) out.set('sort', s.sort);
  if (s.dir !== base.dir) out.set('dir', s.dir);
  if (s.size !== base.size) out.set('size', String(s.size));
  if (s.text.trim()) out.set('q', s.text.trim());
  if (s.ids.length) out.set('ids', s.ids.join(','));
  return out;
}

// ---------------------------------------------------------------- the review window

const DAY_MS = 86_400_000;
export const REVIEW_DUE_DAYS = 30;

/**
 * Today and the end of the window, in the terms a review date is stored in:
 * the reader's calendar day as UTC midnight. See list.reviewSort.
 */
export function reviewWindowMs(now: Date): { today: number; dueEnd: number } {
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return { today, dueEnd: today + REVIEW_DUE_DAYS * DAY_MS };
}

// ---------------------------------------------------------------- the database

export type Where = [field: string, op: '==' | 'in' | 'array-contains' | '<' | '<=' | '>=', value: string | number | boolean | readonly string[]];
export interface QueryShape {
  /** Under the organization. The owner queues read the ownership records; everything else reads the policies. */
  collection: 'policies' | 'policyOwnership';
  where: Where[];
  /** The chosen order, then the document id so a cursor is never ambiguous. */
  orderBy: [field: string, dir: 'asc' | 'desc'][];
}

const SORT_FIELD: Record<LibrarySort, string> = {
  title: 'list.titleSort',
  number: 'list.numberSort',
  review: 'list.reviewSort',
  updated: 'updatedAt',
};

/** What an owner queue's view is called in the ownership record. */
const QUEUE_STATE = { ownerUnassigned: 'unassigned', ownerInactive: 'inactive' } as const;

export function shapeFor(asked: LibraryState, who: Who, now: Date): QueryShape {
  const s = settle(asked, who);
  if (isOwnerQueue(s.view)) {
    return {
      collection: 'policyOwnership',
      where: [
        ['status', 'in', ACTIVE_STATUSES], ['ownerState', '==', QUEUE_STATE[s.view]],
        ...(s.owner ? [['ownerId', '==', s.owner] as Where] : []),
      ],
      orderBy: [[s.sort === 'number' ? 'numberSort' : 'titleSort', s.dir], ['__name__', s.dir]],
    };
  }
  const where: Where[] = [];
  const { today, dueEnd } = reviewWindowMs(now);

  switch (s.view) {
    case 'all': where.push(['status', 'in', ACTIVE_STATUSES]); break;
    case 'published': where.push(['status', '==', 'published']); break;
    case 'inReview': where.push(['status', 'in', IN_REVIEW_STATUSES]); break;
    case 'drafts': where.push(['status', '==', 'draft']); break;
    case 'retired': where.push(['status', '==', 'retired']); break;
    case 'reviewOverdue':
      where.push(['status', '==', 'published'], ['list.reviewSort', '<', today]);
      break;
    case 'reviewDue':
      where.push(['status', '==', 'published'], ['list.reviewSort', '>=', today], ['list.reviewSort', '<=', dueEnd]);
      break;
  }

  if (s.department) where.push(['list.departmentIds', 'array-contains', s.department]);
  if (s.category) where.push(['list.categoryId', '==', s.category]);
  if (s.documentType) where.push(['list.documentTypeId', '==', s.documentType]);
  if (s.mine) where.push(['list.ownerId', '==', who.uid]);
  else if (s.owner) where.push(['list.ownerId', '==', s.owner]);

  return { collection: 'policies', where, orderBy: [[SORT_FIELD[s.sort], s.dir], ['__name__', s.dir]] };
}

// ---------------------------------------------------------------- the search index

export interface SearchShape {
  /** AND across groups, OR within one, as the index takes them. */
  facetFilters: string[][];
  numericFilters: string[];
  hitsPerPage: PageSize;
}

/**
 * The same view, for the search index, or why it cannot answer.
 *
 * The organization and what the role may find are NOT here: they are signed
 * into the search key by the backend (functions/src/search/searchScope.ts)
 * and cannot be widened from this side.
 */
export function searchShapeFor(asked: LibraryState, who: Who, now: Date): SearchShape | { unsupported: string } {
  const s = settle(asked, who);
  // Said plainly rather than answered wrongly: these two are not in the index.
  if (s.view === 'retired') return { unsupported: 'Search does not cover retired policies. Clear the search to list them.' };
  if (isOwnerQueue(s.view)) return { unsupported: 'Search cannot be used in this list. Clear the search to see these policies.' };

  const facetFilters: string[][] = [];
  const numericFilters: string[] = [];
  const { today, dueEnd } = reviewWindowMs(now);

  switch (s.view) {
    case 'published': facetFilters.push(['status:published']); break;
    case 'drafts': facetFilters.push(['status:draft']); break;
    case 'inReview': facetFilters.push(IN_REVIEW_STATUSES.map((v) => `status:${v}`)); break;
    case 'reviewOverdue':
      facetFilters.push(['status:published']);
      numericFilters.push(`nextReviewAt < ${today}`);
      break;
    case 'reviewDue':
      facetFilters.push(['status:published']);
      // The index holds the stored moment, not the calendar day: through the end of the last day.
      numericFilters.push(`nextReviewAt >= ${today}`, `nextReviewAt < ${dueEnd + DAY_MS}`);
      break;
    default: break;
  }

  if (s.department) facetFilters.push([`departmentId:${s.department}`]);
  if (s.category) facetFilters.push([`categoryId:${s.category}`]);
  if (s.documentType) facetFilters.push([`documentTypeId:${s.documentType}`]);
  if (s.mine) facetFilters.push([`ownerUid:${who.uid}`]);
  else if (s.owner) facetFilters.push([`ownerUid:${s.owner}`]);

  return { facetFilters, numericFilters, hitsPerPage: s.size };
}

/** With text typed the index answers, in order of relevance, and no other order is offered. */
export function usesSearch(state: LibraryState): boolean {
  return state.text.trim().length >= 2;
}

/** Whether the page is showing a named set and nothing else. */
export function isNamedSet(state: LibraryState): boolean {
  return state.ids.length > 0;
}

/**
 * Where "View policies" goes after an upload: the library, showing exactly
 * the policies that upload made.
 */
export function uploadedListUrl(policyIds: readonly string[]): string {
  const ids = parseIds(policyIds.join(','));
  return ids.length ? `/policies?ids=${ids.join(',')}` : '/policies';
}

/** Whether anything narrows the view beyond its tab: what "Clear filters" clears. */
export function hasFilters(state: LibraryState): boolean {
  return Boolean(state.mine || state.department || state.category || state.documentType || state.owner);
}

/** The same view with nothing narrowing it. */
export function withoutFilters(state: LibraryState): LibraryState {
  return { ...state, mine: false, department: null, category: null, documentType: null, owner: null };
}
